import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { FormField, Input, Select } from '../ui/Form';
import { Button } from '../ui/Button';
import { useAccountingStore } from '../../stores/accountingStore';
import { useFiscalYearStore } from '../../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../../stores/businessUnitStore';
import { useAuthStore } from '../../stores/authStore';
import type { Account } from '../../types/accounting';

/**
 * Mapping SYSCOHADA : classe (1er chiffre du code) → type AccountType.
 * Aligné avec `SYSCOHADA_CLASS_TYPE_MAPPING` côté backend
 * (apps/accounting-service/src/modules/accounts/constants/accounting-framework.enum.ts).
 *
 * Évite à l'utilisateur de devoir choisir manuellement (et risquer de
 * marquer un compte 411000 en "expense" → ventilation bilan/résultat
 * cassée). Le type reste modifiable, on suggère seulement.
 */
const SYSCOHADA_CLASS_TYPE: Record<string, Account['type']> = {
  '1': 'equity',     // Ressources durables (capitaux + dettes LT)
  '2': 'asset',      // Immobilisations
  '3': 'asset',      // Stocks
  '4': 'asset',      // Tiers (la majorité — 401 fournisseurs = liability mais user peut corriger)
  '5': 'asset',      // Trésorerie
  '6': 'expense',    // Charges
  '7': 'revenue',    // Produits
  '8': 'expense',    // Spéciaux HAO (82x/84x/86x = revenue, user corrige)
};

const CLASS_8_REVENUE_PREFIXES = ['82', '84', '86', '88'];

function inferTypeFromCode(code: string): Account['type'] | null {
  const trimmed = (code || '').trim();
  if (!trimmed) return null;
  const cls = trimmed.charAt(0);
  const prefix2 = trimmed.substring(0, 2);
  if (cls === '8' && CLASS_8_REVENUE_PREFIXES.includes(prefix2)) return 'revenue';
  return SYSCOHADA_CLASS_TYPE[cls] ?? null;
}

const CLASS_LABEL: Record<string, string> = {
  '1': 'Classe 1 — Ressources durables',
  '2': 'Classe 2 — Immobilisations',
  '3': 'Classe 3 — Stocks',
  '4': 'Classe 4 — Tiers',
  '5': 'Classe 5 — Trésorerie',
  '6': 'Classe 6 — Charges',
  '7': 'Classe 7 — Produits',
  '8': 'Classe 8 — Comptes spéciaux (HAO)',
};

interface AccountEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  account?: Account | null;
  onSave: (data: Partial<Account>) => Promise<void>;
}

const initialFormData: Partial<Account> = {
  code: '',
  name: '',
  type: 'asset',
  isAnalytic: false,
  allowReconciliation: false,
  description: ''
};

export function AccountEditModal({ isOpen, onClose, account, onSave }: AccountEditModalProps) {
  const { mode } = useAccountingStore();
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit } = useBusinessUnitStore();
  const { organization } = useAuthStore();

  const [formData, setFormData] = useState<Partial<Account>>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeWasManuallySet, setTypeWasManuallySet] = useState(false);

  // Réinitialiser le formulaire quand le modal s'ouvre ou que le compte change
  useEffect(() => {
    if (isOpen) {
      if (account) {
        setFormData({
          code: account.code || '',
          name: account.name || '',
          type: account.type || 'asset',
          isAnalytic: account.isAnalytic || false,
          allowReconciliation: account.allowReconciliation || false,
          description: account.description || '',
          standard: account.standard || mode
        });
        // Édition d'un compte existant → on respecte son type tel quel
        setTypeWasManuallySet(true);
      } else {
        setFormData({
          ...initialFormData,
          standard: mode
        });
        setTypeWasManuallySet(false);
      }
      setError(null);
    }
  }, [isOpen, account, mode]);

  const handleInputChange = (field: keyof Account, value: string | boolean) => {
    setFormData(prev => {
      const next: Partial<Account> = { ...prev, [field]: value };
      // Auto-suggestion du type comptable quand le user tape le code et
      // n'a pas encore touché manuellement au selecteur. Évite les
      // incohérences class/type (ex : compte 411000 marqué "expense" →
      // ventilation bilan cassée). Si user change le type ensuite,
      // `typeWasManuallySet` bloque l'auto-suggestion.
      if (field === 'code' && typeof value === 'string' && !typeWasManuallySet) {
        const inferred = inferTypeFromCode(value);
        if (inferred) next.type = inferred;
      }
      if (field === 'type') {
        setTypeWasManuallySet(true);
      }
      return next;
    });
    setError(null);
  };

  // Suggestion textuelle visible quand user a tapé un code et que le type
  // qu'il a (manuellement ou par défaut) ne matche pas la classe SYSCOHADA.
  const codeFirstChar = (formData.code || '').charAt(0);
  const expectedTypeForClass = inferTypeFromCode(formData.code || '');
  const typeMismatch =
    expectedTypeForClass && formData.type && formData.type !== expectedTypeForClass;
  const classHint = codeFirstChar && CLASS_LABEL[codeFirstChar] ? CLASS_LABEL[codeFirstChar] : null;

  const validateForm = (): boolean => {
    if (!formData.code?.trim()) {
      setError('Le code du compte est requis');
      return false;
    }
    if (!formData.name?.trim()) {
      setError('Le libellé du compte est requis');
      return false;
    }
    if (!formData.type) {
      setError('Le type de compte est requis');
      return false;
    }
    // Validation du format du code (6 chiffres pour SYSCOHADA)
    if (!/^\d{3,6}$/.test(formData.code.trim())) {
      setError('Le code doit être composé de 3 à 6 chiffres');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Préparer les données avec les champs requis par l'API
      //
      // BUG corrigé : avant, `companyId: selectedBusinessUnit?.id || 'default-company'`
      // mélangeait l'ID de l'unité commerciale (branche/coopérant) avec celui
      // de la société. Pour une coopérative, ça envoyait l'UUID du coopérant
      // dans le champ `companyId` → @IsUUID() passait mais le compte se
      // créait sur la mauvaise entité. Le fallback `'default-company'` était
      // de plus rejeté par @IsUUID(). La SOURCE CORRECTE de companyId est
      // `useAuthStore().organization.id` (rattachement profond du user).
      const accountData: Partial<Account> & { companyId?: string; fiscalYearId?: string } = {
        ...formData,
        code: formData.code?.trim(),
        name: formData.name?.trim(),
        standard: mode,
        fiscalYearId: currentFiscalYear?.id,
        companyId: organization?.id,
        // L'unité commerciale, elle, vient bien de la sidebar (selectedBusinessUnit)
        businessUnitId: selectedBusinessUnit?.id,
        businessUnitType: selectedBusinessUnit?.type,
      };

      if (!accountData.companyId) {
        setError('Aucune entreprise rattachée à votre profil. Contactez votre administrateur.');
        setLoading(false);
        return;
      }
      if (!accountData.fiscalYearId) {
        setError('Aucun exercice fiscal sélectionné. Choisissez un exercice dans la barre latérale.');
        setLoading(false);
        return;
      }

      await onSave(accountData);
      // Le modal sera fermé par le parent après succès
    } catch (err) {
      console.error('Erreur lors de la sauvegarde du compte:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde du compte');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(initialFormData);
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={account ? 'Modifier le compte' : 'Nouveau compte'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Code comptable" required>
            <Input
              value={formData.code || ''}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder="411000"
              maxLength={6}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Code SYSCOHADA (3-6 chiffres)
            </p>
          </FormField>

          <FormField label="Type de compte" required>
            <Select
              value={formData.type || 'asset'}
              onChange={(e) => handleInputChange('type', e.target.value)}
              disabled={loading}
            >
              <option value="asset">Actif (classes 2-5 SYSCOHADA)</option>
              <option value="liability">Passif (4xx fournisseurs, 16-18 dettes)</option>
              <option value="equity">Capitaux propres (classe 1)</option>
              <option value="revenue">Produits (classe 7, 82x/84x/86x HAO)</option>
              <option value="expense">Charges (classe 6, 81x/83x/85x HAO)</option>
            </Select>
            {classHint && (
              <p className="text-xs text-muted-foreground mt-1">
                {classHint}
              </p>
            )}
            {typeMismatch && expectedTypeForClass && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-start gap-1">
                <span aria-hidden>⚠️</span>
                <span>
                  Pour un compte commençant par <code className="font-mono">{codeFirstChar}</code>, le
                  type usuel est <strong>{
                    expectedTypeForClass === 'asset' ? 'Actif'
                    : expectedTypeForClass === 'liability' ? 'Passif'
                    : expectedTypeForClass === 'equity' ? 'Capitaux propres'
                    : expectedTypeForClass === 'revenue' ? 'Produits'
                    : 'Charges'
                  }</strong>. Vérifie ton choix si ce n'est pas intentionnel (ex&nbsp;: 401 fournisseurs = Passif, 411 clients = Actif).
                </span>
              </p>
            )}
          </FormField>
        </div>

        <FormField label="Libellé du compte" required>
          <Input
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Clients"
            disabled={loading}
          />
        </FormField>

        <FormField label="Description">
          <Input
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description optionnelle du compte"
            disabled={loading}
          />
        </FormField>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="isAnalytic"
            checked={formData.isAnalytic || false}
            onChange={(e) => handleInputChange('isAnalytic', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="isAnalytic" className="text-sm text-gray-700 dark:text-gray-300">
            Compte analytique
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="allowReconciliation"
            checked={formData.allowReconciliation || false}
            onChange={(e) => handleInputChange('allowReconciliation', e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
            disabled={loading}
          />
          <label htmlFor="allowReconciliation" className="text-sm text-gray-700 dark:text-gray-300">
            Autoriser le lettrage
          </label>
          <span className="text-xs text-muted-foreground">(comptes tiers : clients, fournisseurs, personnel)</span>
        </div>

        {/* Info sur l'exercice fiscal */}
        {currentFiscalYear && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-4 py-2 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            Ce compte sera créé pour l'exercice : <strong>{currentFiscalYear.code}</strong>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={loading}
            isLoading={loading}
          >
            {account ? 'Modifier' : 'Créer le compte'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}